import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import {
  Heading,
  BodyText,
  Link,
  Strong,
  Em,
} from '@utilitywarehouse/hearth-react';

export const contentfulRenderOptions = ({
  connectorName,
  partnerName,
  whiteText = false,
  headingSize,
  headingTextAlign,
  bodyTextAlign,
  bodyMarginBottom = '200',
  renderHeadingsAsBodyText = false,
}: {
  connectorName?: string;
  partnerName?: string;
  whiteText?: boolean;
  headingSize?: React.ComponentProps<typeof Heading>['size'];
  headingTextAlign?: React.ComponentProps<typeof Heading>['textAlign'];
  bodyTextAlign?: React.ComponentProps<typeof BodyText>['textAlign'];
  bodyMarginBottom?: React.ComponentProps<typeof BodyText>['marginBottom'];
  renderHeadingsAsBodyText?: boolean;
}): Options => {
  const headingRenderer = (
    as: 'h1' | 'h2' | 'h3' | 'h4',
    children: React.ReactNode,
  ) =>
    renderHeadingsAsBodyText ? (
      <BodyText
        size="lg"
        marginBottom={bodyMarginBottom}
        color={whiteText ? 'inverted' : undefined}
        textAlign={bodyTextAlign}
      >
        {children}
      </BodyText>
    ) : (
      <Heading
        as={as}
        inverted={whiteText}
        size={headingSize}
        textAlign={headingTextAlign}
      >
        {children}
      </Heading>
    );

  return {
    renderNode: {
      [BLOCKS.HEADING_1]: (_, children) => headingRenderer('h1', children),
      [BLOCKS.HEADING_2]: (_, children) => headingRenderer('h2', children),
      [BLOCKS.HEADING_3]: (_, children) => headingRenderer('h3', children),
      [BLOCKS.HEADING_4]: (_, children) => headingRenderer('h4', children),
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <BodyText
          size="lg"
          marginBottom={bodyMarginBottom}
          color={whiteText ? 'inverted' : undefined}
          textAlign={bodyTextAlign}
        >
          {children}
        </BodyText>
      ),
      [INLINES.HYPERLINK]: (node, children) => {
        const uri = node.data.uri as string;

        return (
          <Link href={uri} style={{ color: 'inherit' }}>
            {children}
          </Link>
        );
      },
    },
    renderMark: {
      [MARKS.BOLD]: (text) => <Strong>{text}</Strong>,
      [MARKS.ITALIC]: (text) => <Em>{text}</Em>,
    },
    renderText: (text) => {
      // Replace placeholders
      if (connectorName) {
        text = text.replace(/\{\{CONNECTOR_NAME\}\}/g, connectorName);
      }
      if (partnerName) {
        text = text.replace(/\{\{PARTNER_NAME\}\}/g, partnerName);
        text = text.replace(/\{\{partner_first_name\}\}/g, partnerName);
      }

      // Convert line breaks to JSX br elements
      if (text.includes('\n')) {
        return text.split('\n').map((line, index, array) => (
          <span key={index}>
            {line}
            {index < array.length - 1 && <br />}
          </span>
        ));
      }

      return text;
    },
  };
};
